/**
 * Deterministic states for the headless capture harness.
 *
 * `tools/shot.mjs` drives these to photograph a specific visual condition, so
 * the critic loop compares like with like across iterations. A scenario must
 * be reproducible from the seed alone: no wall-clock reads, no `Math.random`.
 *
 * Registration happens in `main.ts`, which is the only module that can see
 * every subsystem. This file owns the registry and the window hook, nothing
 * else, so it stays free of subsystem imports.
 */

import type { World } from '@/core/types';

export interface ScenarioContext {
  world: World;
  /** Runs one full sim step plus a render, ignoring the real clock. */
  stepOnce(): void;
  /** Places a tower without spending cash. */
  place(archetypeId: string, x: number, y: number, tiers?: [number, number, number]): void;
  /** Spawns `n` of an archetype immediately at the given point. */
  spawn(archetypeId: string, n: number, x?: number, y?: number): void;
  /** Fires a grid shockwave, for photographing lattice deformation. */
  shock(x: number, y: number, strength: number, radius: number): void;
  setPhase(p: World['phase']): void;
  /**
   * Puts the run inside the act that owns `round`: its corridor installed, its
   * round on the counter, its shelf in the shop.
   *
   * Setting `world.round` alone does not do this. The map is installed by
   * `main.ts` at act boundaries, so a scenario that only moved the counter
   * would photograph act VII's rounds on act I's corridor — which is the state
   * the game shipped in, and the thing these scenarios exist to disprove.
   */
  enterAct(round: number): void;
  /** Opens the intermission that follows `finishedRound`, as the run would. */
  actBreak(finishedRound: number): void;
  /** Takes the intermission's `index`-th offer, which begins the next act. */
  takeOffer(index: number): void;
}

export type Scenario = (ctx: ScenarioContext) => void;

const registry = new Map<string, Scenario>();

export function register(name: string, fn: Scenario): void {
  registry.set(name, fn);
}

export function has(name: string): boolean {
  return registry.has(name);
}

export function run(name: string, ctx: ScenarioContext): void {
  const fn = registry.get(name);
  if (!fn) {
    // Fail loudly: a silent miss would photograph the wrong state and the
    // critic would be reviewing a lie.
    throw new Error(`[scenarios] unknown scenario "${name}". Known: ${[...registry.keys()].join(', ')}`);
  }
  fn(ctx);
}

export function names(): string[] {
  return [...registry.keys()].sort();
}

/**
 * The surface `tools/shot.mjs` drives. Installed by `main.ts` once every
 * subsystem is constructed, which is also when `ready` flips true.
 */
export interface DevHook {
  ready: boolean;
  world: World;
  scenario(name: string): void;
  advanceFrames(n: number): void;
  list(): string[];
  /** Live post-processing tuning, so the critic can bisect a look. */
  post(key: string, value: number): void;
  /** Frees the loop after a capture run. */
  resume(): void;
}

declare global {
  interface Window { __pixeltd?: DevHook }
}

export function install(hook: DevHook): void {
  window.__pixeltd = hook;
}
